import { useState } from 'react';
import { MatchedGuestProfiles } from '@mews-commander/guest-profile';
import { lt, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Stack, Typography } from '@optimus-web/core';
import { matchAndMergeTestIds } from '../../../../constants';
import { MergeMode } from '../../../../types';
import { ConfirmationDialog, FieldsGrid, HandleChangeParams, ProfileIdsRecord, ReviewOptions } from './components';
import { getFieldValue } from './components/getFieldValue';
import {
    SubmitHandlerParams,
    useConflictingFieldsFormControls,
    useMergeSubmitHandler,
    usePreSelectedInitialProfileIds,
    useProfileSelection,
} from './hooks';

export interface ConflictingFieldsProps extends Pick<SubmitHandlerParams, 'navigateToNextMatch'> {
    mergePossible: boolean;
    mergeMode: MergeMode;
    matchedGuestProfiles: MatchedGuestProfiles;
    readOnlyForm: boolean;
}

export const ConflictingFields = ({
    mergePossible,
    matchedGuestProfiles,
    mergeMode,
    navigateToNextMatch,
    readOnlyForm,
}: ConflictingFieldsProps) => {
    const { profiles: matchingProfiles, preSelectedFields } = matchedGuestProfiles;
    const [leftProfile, rightProfile] = matchingProfiles;
    const [targetId, setTargetId] = useState<string>(leftProfile.id);
    const defaultIds = usePreSelectedInitialProfileIds({ preSelectedFields });
    const [profileIdsSelectedRecord, setProfileIdsSelectedRecord] = useState<ProfileIdsRecord>(defaultIds);

    const mergeDisabled = !mergePossible || readOnlyForm;

    const [{ active: confirmationDialogActive, onClose: handleConfirmationDialogClose }, openConfirmationDialog] =
        useDialog();
    const [{ active: keepSeparateDialogActive, onClose: handleKeepSeparateDialogClose }, openKeepSeparateDialog] =
        useDialog();

    const { handleDeclineMatch, handleMerge, handleReviewLater, isDeclineMatchPending, isMerging } =
        useMergeSubmitHandler({
            mergeMode,
            handleConfirmationDialogClose,
            handleKeepSeparateDialogClose,
            leftProfile,
            rightProfile,
            navigateToNextMatch,
            matchedGuestProfiles,
        });

    const { renderFormContent, setValue, submit } = useConflictingFieldsFormControls({
        preSelectedFields,
        leftProfile,
        rightProfile,
        targetId,
        handleMerge,
    });

    const handleChange = ({ fieldName, value, profileId }: HandleChangeParams) => {
        setValue(fieldName, { value });
        setProfileIdsSelectedRecord((prev: ProfileIdsRecord) => ({ ...prev, [fieldName]: { profileId } }));
    };

    const handleSelectProfile = (profileId: string) => {
        const profile = profileId === leftProfile.id ? leftProfile : rightProfile;
        preSelectedFields.forEach(({ field: fieldName }) => {
            const value = getFieldValue({ profile, fieldName });
            handleChange({ fieldName, value, profileId });
        });
    };

    const selectedProfileId = useProfileSelection(profileIdsSelectedRecord);

    const onChangeEmptySelect = (value: string) => {
        setTargetId(value);
    };

    return (
        <Stack vertical spacing="300" itemAlignment="center" fill>
            <Typography textStyle="titleMediumStrong" variant="primary" align="center" as="div">
                {lt('AccountMergeConflictingFieldsTitle')}
            </Typography>
            <Typography textStyle="titleSmall" variant="primary" align="center" as="div">
                {lt('AccountMergeConflictingFieldsDescription')}
            </Typography>
            <Stack vertical fill>
                {renderFormContent(
                    <>
                        <FieldsGrid
                            handleChange={handleChange}
                            leftProfile={leftProfile}
                            mergeDisabled={mergeDisabled}
                            onChangeEmptySelect={onChangeEmptySelect}
                            onSelectAll={handleSelectProfile}
                            preSelectedFields={preSelectedFields}
                            rightProfile={rightProfile}
                            selectedProfileId={selectedProfileId}
                            targetId={targetId}
                            profileIdsSelectedRecord={profileIdsSelectedRecord}
                        />
                        <Stack alignment="end" spacing="200">
                            <ReviewOptions
                                disabled={readOnlyForm || isDeclineMatchPending}
                                mergeMode={mergeMode}
                                onKeepSeparate={openKeepSeparateDialog}
                                onReviewLater={handleReviewLater}
                            />
                            <Button
                                variant="primary"
                                type="button"
                                data-test-id={matchAndMergeTestIds.mergeButton}
                                onClick={openConfirmationDialog}
                                disabled={mergeDisabled || isDeclineMatchPending || !targetId}
                            >
                                {lt('Merge')}
                            </Button>
                        </Stack>
                        <ConfirmationDialog
                            active={confirmationDialogActive}
                            description={TextKey.AccountMergeConfirmationDialogDescription}
                            isLoading={isMerging}
                            onConfirm={submit}
                            onClose={handleConfirmationDialogClose}
                            primaryButtonText={TextKey.Merge}
                            title={TextKey.AccountMergeConfirmationDialogTitle}
                        />
                        <ConfirmationDialog
                            active={keepSeparateDialogActive}
                            description={TextKey.AccountMergeKeepSeparateConfirmationDialogDescription}
                            isLoading={isDeclineMatchPending}
                            onConfirm={handleDeclineMatch}
                            onClose={handleKeepSeparateDialogClose}
                            primaryButtonText={TextKey.AccountMergeKeepSeparate}
                            title={TextKey.AccountMergeKeepSeparateSuccessTitle}
                        />
                    </>
                )}
            </Stack>
        </Stack>
    );
};
